import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useApp } from '../context/AppContext';
import api from '../api';
import { SHOW_PRICES, TWINT_QR } from '../config';
import { Trash2, Plus, Minus, ShoppingBag, CheckCircle2, Smartphone, MapPin, Clock, KeyRound, Building2, Navigation, Info } from 'lucide-react';

function FridgeInfo({ fridge, t, tf, withMap = true }) {
  if (!fridge) return null;
  return (
    <div data-testid="fridge-info-card" className="rounded-xl border border-[#d8dcc6] bg-[#f6f4ec] overflow-hidden text-left">
      {withMap && (
        <iframe
          title={fridge.name}
          className="w-full h-40 border-0"
          loading="lazy"
          src={`https://www.google.com/maps?q=${encodeURIComponent(fridge.address)}&output=embed`}
        />
      )}
      <div className="p-4 space-y-1.5">
        <p className="font-display text-lg text-[#2f3826]">{fridge.name}</p>
        <p className="flex items-start gap-2 text-sm text-neutral-600"><MapPin size={15} className="text-[#b8863b] mt-0.5 shrink-0" /> {fridge.address}</p>
        {fridge.hours && <p className="flex items-center gap-2 text-sm text-neutral-600"><Clock size={15} className="text-[#b8863b] shrink-0" /> {t('hours')}: {fridge.hours}</p>}
        {fridge.floor && <p className="flex items-center gap-2 text-sm text-neutral-600"><Building2 size={15} className="text-[#b8863b] shrink-0" /> {t('floor_label')}: {fridge.floor}</p>}
        {fridge.access_code && (
          <p data-testid="fridge-access-code" className="flex items-center gap-2 text-sm text-neutral-700">
            <KeyRound size={15} className="text-[#b8863b] shrink-0" /> {t('access_code')}: <b className="text-[#4b5e34] tracking-widest">{fridge.access_code}</b>
          </p>
        )}
        {fridge.instructions && tf(fridge.instructions) && (
          <p className="text-sm text-neutral-600 pt-1 border-t border-[#e4ddcb]">{tf(fridge.instructions)}</p>
        )}
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fridge.address)}`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#4b5e34] bg-[#eef0e6] hover:bg-[#e4ddcb] px-3 py-1.5 rounded-lg transition-colors mt-1"
        >
          <Navigation size={13} /> {t('directions')}
        </a>
      </div>
    </div>
  );
}

// Remembered checkout details, so a returning customer picks meals and pays
// instead of retyping seven fields. This holds CONTACT DETAILS ONLY and lives
// in the customer's own browser. No payment data is stored here or anywhere
// else on this site — cards belong to the payment provider and never touch us.
const REMEMBER_KEY = 'metio_customer_v1';

function loadRemembered() {
  try {
    const raw = window.localStorage.getItem(REMEMBER_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === 'object' ? v : null;
  } catch (_) {
    return null; // private window, blocked storage — just don't pre-fill
  }
}

function saveRemembered(form, pickupDate) {
  try {
    // The collection DAY is stored as a weekday, not a date: dates move every
    // week, "I always collect on Tuesday" does not.
    let weekday = null;
    if (pickupDate) {
      const d = new Date(`${pickupDate}T12:00:00`);
      if (!Number.isNaN(d.getTime())) weekday = d.getDay();
    }
    window.localStorage.setItem(REMEMBER_KEY, JSON.stringify({
      firstname: form.firstname, name: form.name, email: form.email,
      phone: form.phone, building: form.building, pickup: form.pickup, weekday,
    }));
  } catch (_) { /* not important enough to fail an order over */ }
}

function forgetRemembered() {
  try { window.localStorage.removeItem(REMEMBER_KEY); } catch (_) { /* ignore */ }
}

export default function CheckoutPage() {
  const { cart, getDish, tf, t, lang, updateQty, removeFromCart, cartTotal, clearCart, fridges: FRIDGES, menuState, settings, company, isCompany, companyToken } = useApp();
  const navigate = useNavigate();
  const remembered = React.useRef(loadRemembered()).current;
  const [form, setForm] = useState({
    firstname: remembered?.firstname || '', name: remembered?.name || '',
    email: remembered?.email || '', phone: remembered?.phone || '',
    pickup: remembered?.pickup || '', pickupDate: '',
    building: remembered?.building || '', notes: '',
  });
  const [recognised, setRecognised] = useState(Boolean(remembered?.email));
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (FRIDGES.length === 1) setForm((f) => (f.pickup ? f : { ...f, pickup: FRIDGES[0].id }));
  }, [FRIDGES]);
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [preorder, setPreorder] = useState(menuState);
  const [verifying, setVerifying] = useState(false);
  const [payFailed, setPayFailed] = useState('');

  // The payment mode is fetched fresh on every visit and the page shows NO
  // payment block until it is known. The server decides — never the client,
  // and never a cached answer: a stale /api/settings once showed the TWINT QR
  // while the server had moved to Payrexx, and a customer paid twice over.
  const [payModeFetched, setPayModeFetched] = useState(null);   // 'twint_qr' | 'payrexx' | null
  const [payModeError, setPayModeError] = useState(false);
  const [payModeTry, setPayModeTry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setPayModeError(false);
    api.getSettingsFresh()
      .then((s) => { if (!cancelled) setPayModeFetched(s?.paymentMode === 'payrexx' ? 'payrexx' : s?.paymentMode === 'twint_qr' ? 'twint_qr' : null); if (!cancelled && !s?.paymentMode) setPayModeError(true); })
      .catch(() => { if (!cancelled) setPayModeError(true); });
    return () => { cancelled = true; };
  }, [payModeTry]);
  const payMode = payModeFetched;
  const payModeKnown = isCompany || !!payMode;
  // A company client is invoiced monthly, so no online payment path applies —
  // not Payrexx, and not the TWINT tick box either.
  const isPayrexx = payMode === 'payrexx' && !isCompany;
  const [modeChanged, setModeChanged] = useState(false);

  // Coming back from Payrexx: the return URL carries the payment token. The
  // browser is not trusted to say whether it was paid — the server re-reads the
  // gateway from Payrexx and answers.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const outcome = q.get('payment');
    const token = q.get('t');
    if (!outcome || !token) return;
    window.history.replaceState({}, '', '/commande');
    if (outcome !== 'success') {
      setPayFailed(outcome === 'cancel' ? 'cancel' : 'failed');
      return;
    }
    let stop = false;
    let tries = 0;
    setVerifying(true);
    const poll = async () => {
      if (stop) return;
      tries += 1;
      try {
        const r = await api.getPaymentStatus(token);
        if (r.paid) {
          setConfirmed({
            ref: r.ref,
            fridge: FRIDGES.find((f) => f.id === r.pickup) || null,
            name: r.name || '',
            email: r.email || form.email || '',
            itemsText: (r.items || []).map((i) => `${i.qty}× ${i.name}`).join(', '),
          });
          clearCart();
          setVerifying(false);
          window.scrollTo(0, 0);
          return;
        }
      } catch (_) { /* keep polling; the gateway may not have settled yet */ }
      if (tries >= 12) { setVerifying(false); setPayFailed('pending'); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; };
  }, [FRIDGES]); // eslint-disable-line react-hooks/exhaustive-deps

  // Next week's meals need a collection day; the allowed days and the cutoff are
  // decided by the server so the two cannot drift apart.
  const hasPreorder = cart.some((i) => getDish(i.id)?.availability === 'week2');
  useEffect(() => {
    if (!hasPreorder) return;
    api.getPreorderWindow().then(setPreorder).catch(() => {});
  }, [hasPreorder]);

  // Pre-select their usual collection weekday against this week's actual dates.
  useEffect(() => {
    if (!recognised || remembered?.weekday == null) return;
    if (!preorder?.dates?.length) return;
    setForm((f) => {
      if (f.pickupDate) return f;
      const match = preorder.dates.find((d) => new Date(`${d}T12:00:00`).getDay() === remembered.weekday);
      return match ? { ...f, pickupDate: match } : f;
    });
  }, [preorder, recognised, remembered]);

  const forgetMe = () => {
    forgetRemembered();
    setRecognised(false);
    setForm({ firstname: '', name: '', email: '', phone: '', pickup: FRIDGES.length === 1 ? FRIDGES[0].id : '', pickupDate: '', building: '', notes: '' });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit = cart.length > 0 && form.firstname && form.name && form.email && form.phone && form.pickup
    && (!hasPreorder || (preorder.open && form.pickupDate && form.building))
    && payModeKnown && (isCompany || isPayrexx || paid) && !submitting;
  const selectedFridge = FRIDGES.find((f) => f.id === form.pickup);
  const groups = cart.reduce((acc, i) => {
    const d = getDish(i.id);
    if (d && TWINT_QR[d.category]) acc[d.category] = (acc[d.category] || 0) + i.qty;
    return acc;
  }, {});

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const items = cart.map((i) => {
      const d = getDish(i.id);
      return { id: i.id, name: d ? tf(d.name) : '', qty: i.qty, price: d ? d.price : 0 };
    });
    const fridge = selectedFridge;
    const fullName = `${form.firstname} ${form.name}`.trim();
    const itemsText = items.map((i) => `${i.qty}× ${i.name}`).join(', ');
    const payload = { items, name: fullName, email: form.email, phone: form.phone, pickup: form.pickup, pickupDate: hasPreorder ? form.pickupDate : '', building: form.building, notes: form.notes, lang, companyToken: companyToken || '',
      // What this page displayed; the server refuses (409) if it would now do otherwise.
      clientPaymentMode: isCompany ? '' : (payMode || '') };
    setOrderError('');
    setModeChanged(false);
    try {
      saveRemembered(form, hasPreorder ? form.pickupDate : '');
      const res = await api.createOrder(payload);
      if (res.paymentMode === 'payrexx' && res.paymentUrl) {
        // The order exists but is unpaid; it is confirmed only when Payrexx
        // says it was paid. The cart is kept until then so a cancelled
        // payment does not leave the customer with nothing to retry.
        window.location.assign(res.paymentUrl);
        return;
      }
      setConfirmed({ ref: res.ref, fridge, name: fullName, email: form.email || '', itemsText });
      clearCart();
      window.scrollTo(0, 0);
    } catch (err) {
      // Never confirm an order the server did not accept: the customer would walk
      // away with a reference number for an order that was never recorded.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) setModeChanged(true);
      setOrderError(
        typeof detail === 'string' && detail
          ? detail
          : "Votre commande n'a pas pu être enregistrée. Merci de réessayer ou de nous contacter."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#faf9f5]">
        <Header />
        <div data-testid="checkout-verifying" className="max-w-[600px] mx-auto px-5 py-24 text-center">
          <div className="w-12 h-12 mx-auto mb-6 rounded-full border-[3px] border-[#d8dcc6] border-t-[#4b5e34] animate-spin" />
          <h1 className="font-display text-2xl text-[#2f3826] mb-2">{t('verifying_payment')}</h1>
          <p className="text-neutral-600">{t('verifying_wait')}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-[#faf9f5]">
        <Header />
        <div className="max-w-[640px] mx-auto px-5 py-20 text-center">
          <CheckCircle2 size={64} className="mx-auto text-[#5a8a3a] mb-5" />
          <h1 className="font-display text-4xl text-[#2f3826] mb-3">{t('order_confirmed')}</h1>
          <p className="text-neutral-600 mb-6">{t('order_confirmed_msg')}</p>
          <div className="bg-white rounded-2xl border border-neutral-200 p-6 text-left max-w-md mx-auto">
            <p className="text-sm text-neutral-500">{t('order_ref')}</p>
            <p data-testid="order-ref" className="font-display text-2xl text-[#4b5e34] mb-4">{confirmed.ref}</p>
            <p className="text-sm text-neutral-500 mb-2">{t('pickup')}</p>
            <FridgeInfo fridge={confirmed.fridge} t={t} tf={tf} />
          </div>
          {(() => {
            // The customer is never asked to forward their own confirmation.
            // When mail is configured the server has just emailed them, so we
            // say so. Until it is, WhatsApp is offered as an optional way to
            // reach us — not as a step they have to perform. The block
            // disappears on its own once RESEND_API_KEY is set: no second
            // deploy, and no window in which nobody is notified.
            const mailed = settings?.emailConfigured && !!confirmed.email;
            if (mailed) {
              return (
                <p data-testid="confirm-email-sent" className="text-sm text-neutral-600 max-w-md mx-auto mt-5">
                  {t('confirm_email_sent').replace('{email}', confirmed.email)}
                </p>
              );
            }
            const waText = encodeURIComponent(
              `Commande ${confirmed.ref} — ${confirmed.name}\n${confirmed.itemsText}\nRetrait: ${confirmed.fridge?.name || ''}`
            );
            return (
              <div data-testid="whatsapp-confirm-section" className="bg-white rounded-2xl border border-neutral-200 p-6 max-w-md mx-auto mt-5">
                <p className="text-sm text-neutral-600 mb-1">{t('confirm_no_email')}</p>
                <p className="text-sm text-neutral-600 mb-3">{t('send_whatsapp_note')}</p>
                <a
                  href={`https://wa.me/41772426165?text=${waText}`}
                  target="_blank" rel="noreferrer"
                  data-testid="send-whatsapp-btn"
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb457] text-white py-3 rounded-lg font-medium transition-colors"
                >
                  <Smartphone size={18} /> {t('send_whatsapp')}
                </a>
              </div>
            );
          })()}
          <div className="mt-8">
            <Link to="/" className="inline-block bg-[#4b5e34] hover:bg-[#3f5230] text-white px-6 py-3 rounded-lg font-medium transition-colors">{t('new_order')}</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f5]">
      <Header />
      <div className="max-w-[1100px] mx-auto px-5 py-10">
        <h1 className="font-display text-[36px] text-[#2f3826] mb-8">{t('cart_title')}</h1>

        {cart.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag size={44} className="mx-auto text-neutral-300 mb-4" />
            <p className="text-neutral-500 mb-5">{t('cart_empty')}</p>
            <Link to="/?tab=week2" className="inline-block bg-[#4b5e34] hover:bg-[#3f5230] text-white px-6 py-3 rounded-lg font-medium transition-colors">{t('order_next_week')}</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 items-start">
            {/* items */}
            <div className="space-y-3 min-w-0">
              {cart.map((item) => {
                const d = getDish(item.id);
                if (!d) return null;
                return (
                  <div key={item.id} className="flex gap-4 bg-white rounded-2xl border border-neutral-200 p-3">
                    <img src={d.image} alt={tf(d.name)} className="w-20 h-20 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#2f3826] truncate">{tf(d.name)}</h3>
                      {SHOW_PRICES && <p className="text-sm text-neutral-500">CHF {d.price.toFixed(2)}</p>}
                      <div className="mt-2 flex items-center gap-3">
                        <div className="flex items-center border border-neutral-300 rounded-lg">
                          <button onClick={() => updateQty(item.id, item.qty - 1)} className="p-1.5 hover:bg-neutral-100"><Minus size={14} /></button>
                          <span className="w-8 text-center text-sm font-semibold">{item.qty}</span>
                          <button onClick={() => updateQty(item.id, item.qty + 1)} className="p-1.5 hover:bg-neutral-100"><Plus size={14} /></button>
                        </div>
                        <button onClick={() => removeFromCart(item.id)} className="text-neutral-400 hover:text-red-500 flex items-center gap-1 text-sm"><Trash2 size={15} /> {t('remove')}</button>
                      </div>
                    </div>
                    {SHOW_PRICES && <div className="font-semibold text-[#2f3826]">CHF {(d.price * item.qty).toFixed(2)}</div>}
                  </div>
                );
              })}
            </div>

            {/* checkout form */}
            <form onSubmit={submit} className="min-w-0 bg-white rounded-2xl border border-neutral-200 p-6 space-y-4 lg:sticky lg:top-24">
              {SHOW_PRICES && (
                <>
                  <div className="flex justify-between text-sm"><span className="text-neutral-500">{t('subtotal')}</span><span>CHF {cartTotal.toFixed(2)}</span></div>
                  <div className="flex justify-between font-semibold text-lg border-b border-neutral-100 pb-4"><span>{t('total')}</span><span className="text-[#4b5e34]">CHF {cartTotal.toFixed(2)}</span></div>
                </>
              )}

              <h3 className="font-display text-lg text-[#2f3826] pt-1">{t('your_details')}</h3>
              {recognised && (
                <div data-testid="checkout-recognised" className="flex items-start justify-between gap-3 rounded-lg border border-[#d8dcc6] bg-[#f6f4ec] px-3 py-2 text-xs text-[#2f3826]">
                  <span>{t('welcome_back')}</span>
                  <button type="button" onClick={forgetMe} className="shrink-0 underline text-neutral-500 hover:text-[#4b5e34] whitespace-nowrap">
                    {t('not_you')} {t('forget_me')}
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <input required data-testid="input-firstname" placeholder={t('firstname')} value={form.firstname} onChange={set('firstname')} className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]" />
                <input required data-testid="input-name" placeholder={t('name')} value={form.name} onChange={set('name')} className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]" />
              </div>
              <input required type="email" data-testid="input-email" placeholder={t('email')} value={form.email} onChange={set('email')} className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]" />
              <input required data-testid="input-phone" placeholder={t('phone')} value={form.phone} onChange={set('phone')} className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]" />
              {/* Part of the customer's own details, not the collection arrangements:
                  it says where they work, which is how a named bag finds them. */}
              <input required={hasPreorder} data-testid="input-building" placeholder={t('building')} value={form.building} onChange={set('building')} className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]" />

              <h3 className="font-display text-lg text-[#2f3826] pt-1">{t('pickup')}</h3>
              <select data-testid="pickup-select" required value={form.pickup} onChange={set('pickup')} className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]">
                <option value="">{t('choose_pickup')}</option>
                {FRIDGES.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              {selectedFridge && <FridgeInfo fridge={selectedFridge} t={t} tf={tf} />}

              {hasPreorder && (
                preorder.open ? (
                  <>
                    <h3 className="font-display text-lg text-[#2f3826] pt-1">{t('pickup_day')}</h3>
                    <select data-testid="pickup-date-select" required value={form.pickupDate} onChange={set('pickupDate')} className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]">
                      <option value="">{t('choose_pickup_day')}</option>
                      {preorder.dates.map((d) => (
                        <option key={d} value={d}>
                          {new Date(`${d}T12:00:00`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-CH', { weekday: 'long', day: '2-digit', month: '2-digit' })}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <p data-testid="preorder-closed" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    {t('preorder_closed')} {t('preorder_closed_remove')}
                  </p>
                )
              )}
              <textarea placeholder={t('notes')} value={form.notes} onChange={set('notes')} rows={2} className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]" />

              {isCompany ? (
                <div data-testid="checkout-company-invoice" className="border border-[#d8dcc6] bg-[#f6f4ec] rounded-xl p-4">
                  <h3 className="font-display text-lg text-[#2f3826] flex items-center gap-2 mb-1">
                    <Building2 size={17} className="text-[#b8863b]" /> {t('company_billed')}
                  </h3>
                  <p className="text-sm text-neutral-600">{t('company_billed_note')}</p>
                  <div className="mt-3 flex items-baseline justify-between border-t border-[#d8dcc6] pt-3">
                    <span className="text-sm font-medium text-[#2f3826]">{company?.name}</span>
                    <span data-testid="company-total" className="font-display text-2xl text-[#4b5e34]">CHF {cartTotal.toFixed(2)}</span>
                  </div>
                  <p className="mt-2 text-xs text-neutral-500">{t('company_who_orders')}</p>
                </div>
              ) : !payModeKnown ? (
                payModeError ? (
                  <div data-testid="payment-mode-error" className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <p>{t('payment_load_failed')}</p>
                    <button type="button" data-testid="payment-mode-retry" onClick={() => setPayModeTry((n) => n + 1)} className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100">
                      {t('retry')}
                    </button>
                  </div>
                ) : (
                  <p data-testid="payment-mode-loading" className="text-sm text-neutral-500 flex items-center gap-2">
                    <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-[#d8dcc6] border-t-[#4b5e34] animate-spin" /> {t('payment_loading')}
                  </p>
                )
              ) : isPayrexx ? (
                (!hasPreorder || preorder.open) && (
                <div data-testid="checkout-payrexx-section" className="border border-[#d8dcc6] bg-[#f6f4ec] rounded-xl p-4">
                  <h3 className="font-display text-lg text-[#2f3826] flex items-center gap-2 mb-1">
                    <Smartphone size={17} className="text-[#b8863b]" /> {t('pay_now')}
                  </h3>
                  <p className="text-sm text-neutral-600">{t('payrexx_note')}</p>
                  {/* The total is always shown here, even when SHOW_PRICES hides
                      prices elsewhere: nobody should be sent to a payment page
                      for an amount they have not been shown. */}
                  <div className="mt-3 flex items-baseline justify-between border-t border-[#d8dcc6] pt-3">
                    <span className="text-sm font-medium text-[#2f3826]">{t('total')}</span>
                    <span data-testid="payrexx-total" className="font-display text-2xl text-[#4b5e34]">CHF {cartTotal.toFixed(2)}</span>
                  </div>
                </div>
                )
              ) : Object.keys(groups).length > 0 && (!hasPreorder || preorder.open) && (
                <div data-testid="checkout-twint-section" className="border border-[#d8dcc6] bg-[#f6f4ec] rounded-xl p-4">
                  <h3 className="font-display text-lg text-[#2f3826] flex items-center gap-2 mb-1">
                    <Smartphone size={17} className="text-[#b8863b]" /> {t('pay_now')}
                  </h3>
                  <p className="text-xs text-neutral-500 mb-3">{t('twint_note')}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(groups).map(([cat, qty]) => {
                      const q = TWINT_QR[cat];
                      return (
                        <div key={cat} data-testid={`twint-qr-${cat}`} className="bg-white border border-neutral-200 rounded-xl p-3 text-center">
                          <img src={q.img} alt={`TWINT ${tf(q.label)}`} className="w-full aspect-square object-contain mb-1" />
                          <p className="text-sm font-semibold text-[#2f3826]">{tf(q.label)}</p>
                          <p className="text-xs text-neutral-500">CHF {q.price.toFixed(2)} · {qty} {t('twint_scan')}</p>
                        </div>
                      );
                    })}
                  </div>
                  <label className="flex items-center gap-2 mt-3 text-sm text-[#2f3826] cursor-pointer">
                    <input type="checkbox" data-testid="paid-checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} className="w-4 h-4 accent-[#4b5e34]" />
                    {t('paid_confirm')}
                  </label>
                </div>
              )}

              {payFailed && (
                <p data-testid="payment-failed" className={`mb-3 rounded-lg border px-3 py-2 text-sm ${payFailed === 'pending' ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-neutral-300 bg-neutral-50 text-neutral-700'}`}>
                  {payFailed === 'cancel' ? t('pay_cancelled') : payFailed === 'pending' ? t('pay_pending') : t('pay_failed_msg')}
                </p>
              )}

              {orderError && (
                <div data-testid="order-error" className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <p>{orderError}</p>
                  {modeChanged && (
                    <button type="button" data-testid="order-error-reload" onClick={() => window.location.reload()} className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100">
                      {t('reload_page')}
                    </button>
                  )}
                </div>
              )}

              {/* Only next week's meals can be pre-ordered — everything else is
                  bought at the fridge. Say so here rather than letting someone
                  hunt for a way to add a dessert that was never orderable. */}
              <p data-testid="no-preorder-extras" className="flex items-start gap-2 rounded-lg border border-[#d8dcc6] bg-[#f6f4ec] px-3 py-2.5 text-xs leading-relaxed text-[#2f3826]">
                <Info size={15} className="mt-0.5 shrink-0 text-[#b8863b]" />
                <span>{t('no_preorder_extras')}</span>
              </p>

              <Link to="/?tab=week2" data-testid="keep-browsing" className="block text-center text-sm text-[#4b5e34] hover:underline">
                + {t('keep_browsing')}
              </Link>

              <button type="submit" disabled={!canSubmit} data-testid="checkout-submit" className="w-full bg-[#4b5e34] hover:bg-[#3f5230] disabled:opacity-40 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-colors">
                {submitting
                  ? t('redirecting')
                  : isCompany
                    ? `${t('place_order')} · CHF ${cartTotal.toFixed(2)}`
                    : isPayrexx
                      ? `${t('pay_and_order')} · CHF ${cartTotal.toFixed(2)}`
                      : t('place_order')}
              </button>
            </form>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
